import React, { useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import { debounce } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import FormControlLabel from '@mui/material/FormControlLabel';
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import Switch from '@mui/material/Switch';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import * as R from 'ramda';
import { func, shape, string } from 'prop-types';

import { isSpecialChar } from 'utils/helpers/formValidations';
import { ALPHANUMERIC } from 'utils/helpers/validationMessages';
import Destination from './Destination';

const LoadingControlDetails = ({
  controlNumber,
  info,
  setControlNumberState,
  setInfo,
  setValidationState,
  validationState,
}) => {
  const { authorizationType, controlNumbers = [] } = info;
  const { addresses, destinationIdName, destinationName, idName, itn } = controlNumber;

  const [checked, setChecked] = useState(false);
  const [isExpanded, setExpanded] = useState(true);
  const [isItnOn, setIsItnOn] = useState(!R.isNil(itn));

  useEffect(() => {
    if (R.not(R.isNil(R.prop('destinationId', controlNumber)))) setChecked(true);
  }, [controlNumber]);

  const handleLinkDestChange = event => {
    if (checked) {
      R.compose(
        setInfo,
        R.curryN(2, R.assoc('controlNumbers')),
        R.always(
          R.update(
            R.findIndex(R.propEq('idName', R.prop('idName', controlNumber)), controlNumbers),
            R.pick(
              ['idName'],
              R.find(R.propEq('idName', R.prop('idName', controlNumber)), controlNumbers)
            ),
            controlNumbers
          )
        )
      )();
    }
    setChecked(event.target.checked);
  };

  const [itnInputState, setItnInputState] = useState(itn || null);

  const handleItnToggleChange = isChecked => {
    setIsItnOn(isChecked);
    if (!isChecked) {
      setItnInputState(null);
      setControlNumberState(R.assoc('itn', null));
      setValidationState('');
    } else setValidationState(null);
  };

  useEffect(() => {
    if (R.and(!R.equals(authorizationType, 'ONETIME'), !R.equals(authorizationType, 'ONELOAD'))) {
      handleItnToggleChange(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authorizationType]);

  const ITN_MAX_CHARS = 15;
  const ERROR_MESSAGE = `ITN must be ${ITN_MAX_CHARS} characters.`;

  const validateItn = R.cond([
    [value => R.not(R.equals(value?.length, ITN_MAX_CHARS)), R.always(ERROR_MESSAGE)],
    [value => isSpecialChar(value), R.always(ALPHANUMERIC)],
    [R.T, R.always('')],
  ]);

  const storeAndValidateInput = value => {
    setControlNumberState(R.assoc('itn', value));
    setValidationState(validateItn(value));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedValidation = useMemo(() => debounce(storeAndValidateInput, 2000), []);

  const handleItnChange = event => {
    setItnInputState(event.target.value);
    debouncedValidation(event.target.value);
  };

  const defaultToEmpty = R.defaultTo('');

  const createAddress = ({ address, city, state, zipCode }) =>
    `${defaultToEmpty(address)}${address ? ',' : ''}${defaultToEmpty(city)}${
      city ? ',' : ''
    }${defaultToEmpty(state)} ${defaultToEmpty(zipCode)}`;

  return (
    <Accordion
      expanded={isExpanded}
      onChange={() => setExpanded(!isExpanded)}
      slotProps={{
        transition: { unmountOnExit: true },
      }}
      sx={{
        '&.Mui-expanded': { marginTop: 2 },
        /* '&:before': { display: 'none' }, */
        backgroundColor: 'transparent',
        boxShadow: 'none',
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          '.MuiAccordionSummary-content': {
            m: 0,
            minHeight: 56,
          },
          '.MuiAccordionSummary-expandIconWrapper': {
            // eslint-disable-next-line sort-keys-fix/sort-keys-fix
            alignSelf: { xs: 'start', sm: 'center' },
            p: 1,
          },
          alignItems: 'flex-start',
          backgroundColor: 'transparent',
        }}
      >
        <Box
          sx={{
            alignItems: 'center',
            display: 'flex',
            flex: 1,
          }}
        >
          <Box
            sx={{
              columnGap: 1,
              display: 'grid',
              // eslint-disable-next-line sort-keys-fix/sort-keys-fix
              gridTemplateColumns: { xs: '0.25fr 0.25fr 1fr', sm: '0.25fr 0.25fr 3fr' },
              gridTemplateRows: 'repeat(1fr)',
              height: '100%',
              // eslint-disable-next-line sort-keys-fix/sort-keys-fix
              pt: { xs: 1.5, sm: 0 },
              rowGap: 0.5,
              width: '100%',
            }}
          >
            <Typography sx={{ alignSelf: 'center', gridColumn: '1' }} variant="subtitle2">
              {idName}
            </Typography>
            <>
              <Typography sx={{ alignSelf: 'center', gridColumn: '2' }} variant="body2">
                {destinationIdName}
              </Typography>
              <Box
                sx={{
                  alignSelf: addresses?.length ? 'end' : 'center',
                  columnGap: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  // eslint-disable-next-line sort-keys-fix/sort-keys-fix
                  gridColumn: { xs: '1 / 4', sm: '3' },
                }}
              >
                <Typography variant="body2">{destinationName}</Typography>
                {!!addresses?.length &&
                  addresses.map(address => (
                    <Typography key={address.address} variant="body2">
                      {createAddress(address)}
                    </Typography>
                  ))}
              </Box>
            </>
          </Box>
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ backgroundColor: 'transparent', py: 0 }}>
        {R.or(R.equals(authorizationType, 'ONETIME'), R.equals(authorizationType, 'ONELOAD')) && (
          <Stack>
            <Box alignItems="center" display="flex">
              <FormControlLabel
                control={
                  <Switch
                    checked={isItnOn}
                    onChange={({ target: { checked: isChecked } }) =>
                      handleItnToggleChange(isChecked)
                    }
                  />
                }
                label="ITN"
                sx={{ mr: 0 }}
              />
              <Tooltip
                enterTouchDelay={10}
                placement="bottom-start"
                slotProps={{
                  popper: {
                    sx: {
                      '& .MuiTooltip-tooltip': {
                        borderRadius: '4px',
                        p: 1,
                      },
                    },
                  },
                }}
                title='Use of the Internal Transaction Number ("ITN") field is recommended for international barrel transactions.'
              >
                <IconButton>
                  <InfoRoundedIcon
                    sx={{
                      color: 'text.disabled',
                      fontSize: 16,
                    }}
                  />
                </IconButton>
              </Tooltip>
            </Box>
            {isItnOn && (
              <TextField
                error={R.and(!R.isEmpty(validationState), !R.isNil(validationState))}
                fullWidth
                helperText={validationState}
                onBlur={() => setValidationState(validateItn(itnInputState))}
                onChange={handleItnChange}
                sx={{ my: 1 }}
                value={itnInputState || ''}
              />
            )}
          </Stack>
        )}
        <Stack
          direction="row"
          spacing={2}
          sx={{ alignItems: 'center', justifyContent: 'space-between' }}
        >
          <FormControlLabel
            control={<Switch checked={checked} onChange={handleLinkDestChange} />}
            label="Link Destination"
          />
        </Stack>
        {checked && !destinationIdName && (
          <Destination idName={idName} info={info} setExpanded={setExpanded} setInfo={setInfo} />
        )}
      </AccordionDetails>
    </Accordion>
  );
};

LoadingControlDetails.propTypes = {
  controlNumber: shape({}),
  info: shape({}),
  setControlNumberState: func.isRequired,
  setInfo: func.isRequired,
  setValidationState: func.isRequired,
  validationState: string,
};

LoadingControlDetails.defaultProps = {
  controlNumber: {},
  info: {},
  validationState: '',
};

export default LoadingControlDetails;
